import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, GraduationCap, Sparkles, BookOpen, Target, Trophy, ChevronDown, ChevronRight, Brain, Calendar, Play, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import GuestPreview from "@/components/GuestPreview";
import AppellationsInText from "@/components/AppellationsInText";

const COACH_DEMO_STEPS = [
  { title: "1. Fixez votre objectif", body: "« Préparer le WSET 2 en 8 semaines », 15 min/jour", icon: Target, color: "text-[#1F3A5F]" },
  { title: "2. geoVINUM AI construit le plan", body: "Programme adaptatif semaine par semaine, jour par jour", icon: Brain, color: "text-[#E5276A]" },
  { title: "3. Flashcards & révisions", body: "Questions ciblées sur vos points faibles", icon: BookOpen, color: "text-[#E5276A]" },
  { title: "4. Suivi & motivation", body: "geoVINUM AI adapte la difficulté à vos progrès", icon: Trophy, color: "text-[#1F3A5F]" },
];

const PREVIEW_PLAN = {
  title: "Préparer le WSET niveau 2 en 8 semaines",
  duration_weeks: 8,
  weekly_focus: [
    { week_no: 1, theme: "Fondations : viticulture & vinification", milestone: "Comprendre le cycle de la vigne et les 5 étapes clés", daily_session: [
      { kind: "lecture", label: "Lire chapitre 1 du guide WSET", est_min: 7 },
      { kind: "flashcards", label: "10 cartes : cycle végétatif", est_min: 5 },
      { kind: "quiz", label: "Quiz : structure d'une grappe", est_min: 3 },
    ]},
    { week_no: 2, theme: "France : Bordeaux & Bourgogne", milestone: "Maîtriser les hiérarchies d'appellations", daily_session: [
      { kind: "lecture", label: "Cartes Bordeaux rive G/D", est_min: 8 },
      { kind: "flashcards", label: "Classement de 1855", est_min: 4 },
      { kind: "atelier", label: "Compare 2 climats Côte de Nuits", est_min: 3 },
    ]},
    { week_no: 3, theme: "Italie, Espagne, Allemagne", milestone: "Identifier les cépages emblématiques", daily_session: [] },
  ],
  flashcards: [
    { question: "Quels sont les 5 cépages principaux du Bordeaux rouge ?", answer: "Cabernet-Sauvignon, Merlot, Cabernet Franc, Petit Verdot, Malbec.", hint: "Pense à ce qu'on assemble dans un Médoc classique." },
    { question: "Que désigne le terme « climat » en Bourgogne ?", answer: "Une parcelle de vigne précisément délimitée par des conditions géologiques et climatiques uniques. Inscrits à l'UNESCO en 2015." },
  ],
  final_test_topics: [
    "Hiérarchie des AOC françaises (du régional au Grand Cru)",
    "5 cépages signatures par pays viticole",
    "Vinifications particulières : champenoise, macération carbonique, oxydative",
  ],
};

export default function LearnCoach() {
  const { user } = useAuth();
  const { lang } = useT();
  const [goal, setGoal] = useState("Préparer le WSET niveau 2 en 8 semaines");
  const [level, setLevel] = useState("amateur");
  const [minutes, setMinutes] = useState(15);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [open, setOpen] = useState({});
  const [flipped, setFlipped] = useState({});
  const [mine, setMine] = useState([]);
  const [today, setToday] = useState(null);
  const [activating, setActivating] = useState(false);

  useEffect(() => { if (user) { load(); loadToday(); } }, [user]); // eslint-disable-line

  const load = async () => {
    try { const { data } = await api.get("/tlp/learn/coach/mine"); setMine(data); } catch {}
  };

  const loadToday = async () => {
    try { const { data } = await api.get("/tlp/learn/coach/today"); setToday(data); } catch {}
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/learn/coach", { goal, level, minutes_per_day: minutes, lang });
      setPlan(data.plan);
      setPlanId(data.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const activatePlan = async () => {
    if (!planId) return;
    setActivating(true);
    try {
      await api.post(`/tlp/learn/coach/${planId}/activate`);
      toast.success("Plan démarré ! geoVINUM AI vous accompagne chaque jour.");
      await loadToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation impossible");
    } finally { setActivating(false); }
  };

  const toggleTaskDone = async (taskIndex, currentlyDone) => {
    if (currentlyDone) return; // Idempotent: backend ignores re-mark
    try {
      await api.post(`/tlp/learn/coach/today/task/${taskIndex}/done`);
      // Optimistic update
      setToday((t) => t ? { ...t, tasks: t.tasks.map((task, i) => i === taskIndex ? { ...task, done: true } : task) } : t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!user) {
    return (
      <GuestPreview
        kicker="Learn · Coach"
        kickerIcon={GraduationCap}
        title="Votre coach personnel"
        description="Donnez un objectif et le temps disponible chaque jour. geoVINUM AI construit un plan adaptatif semaine par semaine avec flashcards et quiz ciblés sur vos points faibles."
        demoSteps={COACH_DEMO_STEPS}
        nextPath="/learn/coach"
        ctaTitle={<>Un coach <span className="text-[#E5276A]">qui s'adapte à vous</span></>}
        ctaSubtitle="WSET, prépa sommelier, simple progression amateur — peu importe l'objectif. Inscrivez-vous pour générer votre plan personnalisé en 1 minute."
        testId="learn-coach-preview"
      >
        <h3 className="font-display text-xl text-[#1F3A5F]">{PREVIEW_PLAN.title}</h3>
        <p className="text-xs text-[#8B95A1] mt-1 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {PREVIEW_PLAN.duration_weeks} semaines · 15 min/jour</p>

        <div className="mt-5 space-y-2">
          {PREVIEW_PLAN.weekly_focus.slice(0, 2).map((w, i) => (
            <div key={i} className="app-card overflow-hidden">
              <div className="w-full p-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[#E5276A] text-white text-xs font-bold inline-flex items-center justify-center shrink-0">S{w.week_no}</span>
                <div className="min-w-0 text-left flex-1">
                  <div className="font-bold text-[#1F3A5F] text-sm">{w.theme}</div>
                  <div className="text-xs text-[#8B95A1]">{w.milestone}</div>
                </div>
              </div>
              {w.daily_session.length > 0 && (
                <div className="px-3 pb-3 pt-1 border-t border-[#1F3A5F]/8 space-y-1.5">
                  {w.daily_session.map((s, j) => (
                    <div key={j} className="flex items-center gap-2 text-xs">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full">{s.kind}</span>
                      <span className="text-[#1F3A5F] flex-1 truncate">{s.label}</span>
                      <span className="text-[#8B95A1]">{s.est_min} min</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="text-xs text-[#8B95A1] italic text-center pt-2">… et 6 semaines supplémentaires personnalisées dans la version réelle.</div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Flashcards générées</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {PREVIEW_PLAN.flashcards.map((f, i) => (
              <div key={i} className="p-4 rounded-2xl bg-white border-2 border-[#E5276A]/20">
                <div className="text-[10px] uppercase font-bold tracking-wider text-[#E5276A] mb-2">Carte {i + 1}</div>
                <p className="text-sm text-[#1F3A5F] leading-relaxed">{f.question}</p>
                {f.hint && <p className="text-xs text-[#8B95A1] mt-3 italic">💡 {f.hint}</p>}
              </div>
            ))}
          </div>
        </div>
      </GuestPreview>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="learn-coach">
      <span className="chip-pink inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> Learn · Coach</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Votre coach personnel</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">Donnez-moi un objectif et le temps que vous pouvez y consacrer chaque jour — je construis un plan adaptatif semaine par semaine avec flashcards.</p>

      {/* Today's session — appears if a plan is active */}
      {today?.available && (
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#FCE4ED] via-white to-[#FBF7F3] border-2 border-[#E5276A] p-6 md:p-7 relative overflow-hidden" data-testid="coach-today">
          <Sparkles className="absolute -top-6 -right-6 w-32 h-32 text-[#E5276A]/8" />
          <div className="relative">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold uppercase tracking-widest">
                  <Calendar className="w-3 h-3" /> Aujourd'hui · J{today.day_in_week}
                </span>
                <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-3 leading-tight">{today.week_theme}</h2>
                <p className="text-sm text-[#4A5568] mt-1">{today.week_milestone}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-[#E5276A]">{today.minutes_today}<span className="text-base ml-1">min</span></div>
                <div className="text-xs text-[#8B95A1]">Semaine {today.week_no}/{today.total_weeks}</div>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              {today.tasks.map((t, i) => (
                <button
                  key={i}
                  onClick={() => toggleTaskDone(i, t.done)}
                  data-testid={`coach-today-task-${i}`}
                  disabled={t.done}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                    t.done
                      ? "bg-[#FBF7F3] border-emerald-300 opacity-70"
                      : "bg-white border-[#1F3A5F]/12 hover:border-[#E5276A] hover:shadow-md"
                  }`}
                >
                  {t.done ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-[#8B95A1] shrink-0" />
                  )}
                  <span className="text-[10px] uppercase font-bold tracking-wider text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full shrink-0">{t.kind}</span>
                  <span className={`flex-1 text-sm ${t.done ? "text-[#8B95A1] line-through" : "text-[#1F3A5F]"}`}>{t.label}</span>
                  <span className="text-xs text-[#8B95A1] shrink-0">{t.est_min} min</span>
                </button>
              ))}
            </div>

            {today.tasks.every((t) => t.done) && (
              <p className="mt-4 text-center text-sm font-bold text-emerald-600 inline-flex items-center justify-center gap-1.5 w-full">
                <Trophy className="w-4 h-4" /> Bravo, vous avez bouclé la journée !
              </p>
            )}
          </div>
        </div>
      )}

      {today && today.reason === "plan_finished" && (
        <div className="mt-6 rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-5 text-center" data-testid="coach-finished">
          <Trophy className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <h3 className="font-display text-xl text-[#1F3A5F]">Plan terminé !</h3>
          <p className="text-sm text-[#4A5568] mt-1">Vous avez complété {today.duration_weeks} semaines de progression. Générez un nouveau plan ci-dessous pour continuer 🎉</p>
        </div>
      )}

      {today && today.tasks?.length > 0 && (
        <AppellationsInText text={today.tasks.map((t) => t.label).join(" ")} />
      )}

      <div className="mt-8 app-card p-6 grid sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Objectif</span>
          <input value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="coach-goal"
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F]" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Niveau actuel</span>
          <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="coach-level"
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F]">
            <option value="debutant">Découverte</option>
            <option value="amateur">Amateur</option>
            <option value="professionnel">Pro</option>
            <option value="expert">Expert</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Temps par jour (min)</span>
          <input type="number" min="5" max="120" value={minutes} onChange={(e) => setMinutes(parseInt(e.target.value) || 15)} data-testid="coach-minutes"
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F]" />
        </label>
        <button onClick={generate} disabled={busy} data-testid="coach-generate"
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer mon plan (5 crédits)
        </button>
      </div>

      {plan && (
        <div className="mt-10 space-y-6" data-testid="coach-plan">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-3xl text-[#1F3A5F]">{plan.title}</h2>
              <p className="text-sm text-[#8B95A1] mt-1">{plan.duration_weeks} semaines · {minutes} min/jour</p>
            </div>
            {planId && (today?.plan_id !== planId) && (
              <button
                onClick={activatePlan}
                disabled={activating}
                data-testid="coach-activate"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50 shadow-md"
              >
                {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                Démarrer ce plan
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {planId && today?.plan_id === planId && (
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-bold">
                <CheckCircle2 className="w-3.5 h-3.5" /> Plan en cours
              </span>
            )}
          </div>
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Plan semaine par semaine</div>
            <div className="space-y-2">
              {(plan.weekly_focus || []).map((w, i) => (
                <div key={i} className="app-card overflow-hidden" data-testid={`coach-week-${w.week_no}`}>
                  <button onClick={() => setOpen({ ...open, [i]: !open[i] })}
                    className="w-full p-4 flex items-center justify-between gap-3 hover:bg-[#FBF7F3]">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-full bg-[#E5276A] text-white text-xs font-bold inline-flex items-center justify-center shrink-0">S{w.week_no}</span>
                      <div className="min-w-0 text-left">
                        <h3 className="font-bold text-[#1F3A5F] text-sm">{w.theme}</h3>
                        <p className="text-xs text-[#8B95A1]">{w.milestone}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-[#8B95A1] shrink-0 transition-transform ${open[i] ? "rotate-180" : ""}`} />
                  </button>
                  {open[i] && (
                    <div className="px-4 pb-4 pt-2 border-t border-[#1F3A5F]/8 space-y-2">
                      {(w.daily_session || []).map((s, j) => (
                        <div key={j} className="flex items-start gap-3 text-sm">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-[#E5276A] bg-[#FCE4ED] px-2 py-0.5 rounded-full">{s.kind}</span>
                          <span className="text-[#1F3A5F] flex-1">{s.label}</span>
                          <span className="text-xs text-[#8B95A1]">{s.est_min} min</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {plan.flashcards?.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Flashcards (cliquez pour retourner)</div>
              <div className="grid md:grid-cols-2 gap-3">
                {plan.flashcards.map((f, i) => (
                  <button key={i} onClick={() => setFlipped({ ...flipped, [i]: !flipped[i] })} data-testid={`coach-card-${i}`}
                    className={`text-left p-5 rounded-2xl min-h-[140px] transition-all border-2 ${flipped[i] ? "bg-[#1F3A5F] text-white border-[#1F3A5F]" : "bg-white text-[#1F3A5F] border-[#E5276A]/30 hover:border-[#E5276A]"}`}>
                    <div className={`text-[10px] uppercase font-bold tracking-wider mb-2 ${flipped[i] ? "text-[#FFD8E5]" : "text-[#E5276A]"}`}>
                      {flipped[i] ? "Réponse" : `Carte ${i + 1}`}
                    </div>
                    <p className="text-sm leading-relaxed">{flipped[i] ? f.answer : f.question}</p>
                    {!flipped[i] && f.hint && <p className="text-xs text-[#8B95A1] mt-3 italic">💡 {f.hint}</p>}
                  </button>
                ))}
              </div>
            </section>
          )}

          {plan.final_test_topics?.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Sujets clés à maîtriser</div>
              <ul className="space-y-2">
                {plan.final_test_topics.map((t, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#1F3A5F]"><ChevronRight className="w-4 h-4 text-[#E5276A] mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {mine.length > 0 && (
        <div className="mt-12 pt-8 border-t border-[#1F3A5F]/8">
          <h3 className="font-display text-xl text-[#1F3A5F] mb-3">Vos plans précédents</h3>
          <div className="space-y-2">
            {mine.map((p) => (
              <button key={p.id} onClick={() => setPlan(p.plan)} data-testid={`coach-mine-${p.id}`}
                className="w-full text-left app-card p-3 hover:bg-[#FBF7F3] flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1F3A5F] truncate">{p.plan?.title || p.goal}</p>
                  <p className="text-xs text-[#8B95A1]">{p.minutes_per_day} min/j · {p.plan?.duration_weeks || "?"} semaines</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8B95A1] shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
